import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ProductEx } from "@front/m19-models";
import { AccountSelectionService, AsinService } from "@front/m19-services";
import { combineLatest, map, switchMap } from "rxjs";
import { ReviewScoreComponent } from "@m19-board/tracking/review-score/review-score.component";

@Component({
  selector: "app-asin-item-preview",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "flex min-w-0 flex-col gap-1.5",
  },
  template: `
    @if (product()?.imageUrl) {
      <div class="relative h-20 w-full overflow-hidden rounded-lg border">
        <img
          class="absolute inset-0 size-full object-contain"
          [src]="product()?.imageUrl"
          [alt]="product()?.title ?? ''"
        />
      </div>
    } @else {
      <div class="bg-muted h-20 w-full rounded-lg border border-transparent"></div>
    }

    <div class="flex w-full flex-col gap-1.5 px-0.5">
      @if (product()?.title || storePageName()) {
        <p class="type-label h-4 truncate">{{ storePageName() ?? product()?.title }}</p>
      } @else {
        <div class="bg-muted h-3 w-[70%] rounded"></div>
      }

      @if (showRating()) {
        @if (product()?.reviewScore) {
          <app-review-score [score]="product()?.reviewScore!" />
        } @else {
          <div class="bg-muted h-1.75 w-12.5 rounded"></div>
        }
      }
    </div>
  `,
  imports: [ReviewScoreComponent],
})
export class AsinItemPreviewComponent {
  readonly asinService = inject(AsinService);
  readonly accountSelectionService = inject(AccountSelectionService);

  readonly asin = input<string>();
  readonly storePageName = input<string>();
  readonly showRating = input(true);

  readonly product = toSignal<ProductEx | undefined>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        combineLatest([this.asinService.getCatalog(am.accountId, am.marketplace), toObservable(this.asin)]),
      ),
      map(([catalog, asin]) => catalog?.asinOffers?.get(asin ?? "") ?? undefined),
    ),
  );
}
